import { memo, type MouseEvent as ReactMouseEvent, type RefObject } from 'react';
import {
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { ArtifactKindBadge } from '../ArtifactKindBadge';
import { DateTimeCell } from '../DateTimeCell';
import { ArtifactNameCell } from './ArtifactNameCell';
import type { WorkspaceArtifactRow } from './ExistingArtifactsPicker.types';
import { ThumbnailTile } from './ThumbnailTile';
import { useTranslation } from 'react-i18next';

interface Props {
  rows: WorkspaceArtifactRow[];
  isLoading: boolean;
  hasFilter: boolean;
  selectedIds: Set<string>;
  attachedIds: Set<string>;
  selectableIdCount: number;
  allSelectableSelected: boolean;
  someSelectableSelected: boolean;
  /**
   * Scroll element the row virtualizer measures against. Owned by the parent
   * (it wraps the table in the scrollable region), passed down so windowing
   * tracks the same viewport the user scrolls.
   */
  scrollRef: RefObject<HTMLElement | null>;
  /**
   * Widget render-data per row, keyed by `getRowDetailKey(row)`. Populated only
   * for the live-render path (flag on + supported widget kind); empty otherwise,
   * so rows fall back to their static thumbnail / palette icon.
   */
  dataByRowKey: Map<string, Record<string, unknown>>;
  getRowDetailKey: (row: WorkspaceArtifactRow) => string;
  /**
   * Message shown when the list is empty and a search/filter is active.
   * Defaults to "No matches".
   */
  noMatchesMessage?: string;
  /**
   * Message shown when the list is empty and no search/filter is active.
   * Defaults to "No artifacts in workspace"; the project picker overrides it.
   */
  emptyMessage?: string;
  onToggleSelectAll: () => void;
  /**
   * Fires on row click. The full event is forwarded so callers can detect
   * shift/meta/ctrl modifiers for range-select. `index` is the row's position
   * in `rows`, used by the caller to compute a [from, to] range against an
   * anchor.
   */
  onRowActivate: (index: number, event: ReactMouseEvent) => void;
}

/**
 * Approximate rendered height of one picker row (40px thumbnail + 8px×2 cell
 * padding). The virtualizer only uses this to size the scroll spacers; exact
 * pixels don't matter because rows aren't dynamically measured.
 */
const ESTIMATED_ROW_HEIGHT = 56;

interface PickerRowProps {
  row: WorkspaceArtifactRow;
  index: number;
  attachedIds: Set<string>;
  selectedIds: Set<string>;
  dataByRowKey: Map<string, Record<string, unknown>>;
  getRowDetailKey: (row: WorkspaceArtifactRow) => string;
  onRowActivate: (index: number, event: ReactMouseEvent) => void;
}

// Module-scope: used inside `rows.map` (both the windowed and
// non-windowed paths), closes over only the 5 values threaded as props.
// Memoized because the virtualizer re-renders the table on every scroll update;
// without it each mounted row re-renders its cells' tooltip trees as well.
const PickerRow = memo(function PickerRow({
  row,
  index,
  attachedIds,
  selectedIds,
  dataByRowKey,
  getRowDetailKey,
  onRowActivate,
}: PickerRowProps) {
  const { t } = useTranslation('common');

  const isAttached = attachedIds.has(row.id);
  const isSelected = selectedIds.has(row.id);
  return (
    <DatatableRow
      key={row.id}
      state={isSelected || isAttached ? 'selected' : 'default'}
      interactive
      className="focus-within:ring-0 focus-within:ring-offset-0"
      onMouseDown={(event) => {
        // Suppress the browser's default shift-click text selection
        // so range-select reads as a click interaction, not a
        // highlight. Plain clicks still allow normal selection.
        if (event.shiftKey) event.preventDefault();
      }}
      onClick={(event) => {
        if (isAttached) return;
        onRowActivate(index, event);
      }}
    >
      <DatatableCell
        className="pr-0"
        onMouseDown={(event) => {
          if (event.shiftKey) event.preventDefault();
        }}
        onClick={(event) => {
          // Route through `onRowActivate` so shift-click range select
          // works the same whether the user clicks the checkbox or the
          // row body. `stopPropagation` keeps the row's onClick from
          // firing again (it would double-toggle this row).
          event.stopPropagation();
          if (isAttached) return;
          onRowActivate(index, event);
        }}
      >
        <div className="flex items-center justify-end">
          <Checkbox
            aria-label={t('artifact.pickerTable.selectArtifact', { name: row.name })}
            checked={isAttached ? true : isSelected}
            disabled={isAttached}
            className={microInteraction.checkbox}
          />
        </div>
      </DatatableCell>
      <DatatableCell>
        <div className="flex min-w-0 items-center gap-3">
          <ThumbnailTile row={row} data={dataByRowKey.get(getRowDetailKey(row))} />
          <ArtifactNameCell row={row} />
        </div>
      </DatatableCell>
      <DatatableCell>
        <ArtifactKindBadge kind={row.kind} />
      </DatatableCell>
      <DatatableCell>
        <DateTimeCell iso={row.createdAt} />
      </DatatableCell>
    </DatatableRow>
  );
});

export function PickerTable(props: Props) {
  const { t } = useTranslation('common');

  const showEmpty = !props.isLoading && props.rows.length === 0;
  const checkedState: boolean | 'indeterminate' = props.allSelectableSelected
    ? true
    : props.someSelectableSelected
      ? 'indeterminate'
      : false;

  // Windowing: mount only the rows in (or near) the viewport so the picker
  // doesn't mount a live widget per row across an unbounded "Load more" list.
  const virtualizer = useVirtualizer({
    count: props.rows.length,
    getScrollElement: () => props.scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 8,
    // The default renders and commits synchronously on every scroll event, and each
    // row that mounts forces a layout of its own; a fast scroll stacked them into
    // frames of several hundred ms. Batched, a frame commits once.
    useFlushSync: false,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // jsdom (and the first paint before the scroll element is measured) reports a
  // 0-height viewport, which yields an empty virtual window. Fall back to the
  // full list so rows still render where layout isn't available yet.
  const useWindowing = virtualItems.length > 0;
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = useWindowing ? virtualItems[0].start : 0;
  const paddingBottom = useWindowing ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  const renderRow = (row: WorkspaceArtifactRow, index: number) => (
    <PickerRow
      key={row.id}
      row={row}
      index={index}
      attachedIds={props.attachedIds}
      selectedIds={props.selectedIds}
      dataByRowKey={props.dataByRowKey}
      getRowDetailKey={props.getRowDetailKey}
      onRowActivate={props.onRowActivate}
    />
  );

  return (
    <Datatable className="table-fixed **:data-[slot=datatable-cell]:py-2 **:data-[slot=datatable-cell]:text-sm **:data-[slot=datatable-cell]:font-normal **:data-[slot=datatable-head]:text-sm **:data-[slot=datatable-head]:font-medium">
      <DatatableHeader>
        <DatatableRow>
          <DatatableHead className="sticky top-0 z-10 w-[32px] pr-0 bg-background focus-within:z-20">
            <div className="flex items-center justify-end">
              <Checkbox
                aria-label={t('artifact.pickerTable.selectAllArtifacts')}
                checked={checkedState}
                disabled={props.selectableIdCount === 0}
                onCheckedChange={props.onToggleSelectAll}
                className={microInteraction.checkbox}
              />
            </div>
          </DatatableHead>
          <DatatableHead className="sticky top-0 z-10 bg-background">
            {t('artifact.pickerTable.name')}
          </DatatableHead>
          <DatatableHead className="sticky top-0 z-10 w-[160px] bg-background">
            {t('artifact.pickerTable.type')}
          </DatatableHead>
          <DatatableHead className="sticky top-0 z-10 w-[176px] bg-background">
            {t('artifact.pickerTable.dateCreated')}
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {props.isLoading &&
          Array.from({ length: 5 }).map((_, index) => (
            <DatatableRow key={`skeleton-${index}`}>
              <DatatableCell>
                <Skeleton className="size-4" />
              </DatatableCell>
              <DatatableCell>
                <div className="flex items-center gap-3">
                  <Skeleton className="size-10 shrink-0" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              </DatatableCell>
              <DatatableCell>
                <Skeleton className="h-5 w-20 rounded-full" />
              </DatatableCell>
              <DatatableCell>
                <Skeleton className="h-4 w-3/4" />
              </DatatableCell>
            </DatatableRow>
          ))}
        {showEmpty && (
          <DatatableRow>
            <DatatableCell colSpan={4} className="text-center text-muted-foreground">
              {props.hasFilter
                ? (props.noMatchesMessage ?? t('artifact.pickerTable.noMatches'))
                : (props.emptyMessage ?? t('artifact.pickerTable.noArtifactsInWorkspace'))}
            </DatatableCell>
          </DatatableRow>
        )}
        {!props.isLoading && !useWindowing && props.rows.map(renderRow)}
        {!props.isLoading && useWindowing && (
          // Spacer rows reserve the scroll height of the off-screen rows so the
          // scrollbar tracks the full list while only the window mounts. Heights
          // are virtualizer-computed pixel values that change every scroll frame,
          // so inline `style` is the only option (no static Tailwind class fits).
          <>
            {paddingTop > 0 && (
              <tr aria-hidden>
                <td colSpan={4} style={{ height: paddingTop }} />
              </tr>
            )}
            {virtualItems.map((item) => renderRow(props.rows[item.index], item.index))}
            {paddingBottom > 0 && (
              <tr aria-hidden>
                <td colSpan={4} style={{ height: paddingBottom }} />
              </tr>
            )}
          </>
        )}
      </DatatableBody>
    </Datatable>
  );
}
